import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { CreditCard, TrendingUp, Handshake } from 'lucide-react';
interface InvestorSlide6Props {
  isDark?: boolean;
}
export function InvestorSlide6({
  isDark = false
}: InvestorSlide6Props) {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };
  return <motion.div className="h-full w-full overflow-y-auto overflow-x-hidden" variants={containerVariants} initial="hidden" animate="visible" data-id="element-309">
      <div className="min-h-full flex flex-col justify-center max-w-6xl mx-auto px-6 sm:px-12 py-20 sm:py-24" data-id="element-310">
        <motion.div variants={itemVariants} className="mb-12 sm:mb-16 text-center" data-id="element-311">
          <h1 className={`text-3xl sm:text-5xl font-bold mb-6 sm:mb-8 tracking-tight ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-312">
            Business Model
          </h1>
          <p className={`text-lg sm:text-2xl max-w-3xl mx-auto ${isDark ? 'text-white/80' : 'text-heliona-text-gray'}`} data-id="element-313">
            High-margin B2B SaaS with embedded expansion loops
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-10 mb-12 sm:mb-16" data-id="element-314">
          {/* Stream 1 */}
          <motion.div variants={itemVariants} className={`rounded-3xl p-8 sm:p-10 ${isDark ? 'bg-heliona-mint/10 border border-heliona-mint/30' : 'bg-heliona-mint/10 border border-heliona-mint/30'}`} data-id="element-315">
            <div className="flex items-center gap-4 mb-6" data-id="element-316">
              <div className="p-3 rounded-xl bg-heliona-mint/20" data-id="element-317">
                <CreditCard className="w-6 h-6 text-heliona-mint-dark" data-id="element-318" />
              </div>
              <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-319">
                Subscriptions
              </h3>
            </div>
            <ul className={`space-y-3 text-sm sm:text-base ${isDark ? 'text-white/80' : 'text-heliona-text-gray'}`} data-id="element-320">
              <li data-id="element-321">• Per-leader monthly/annual SaaS fee</li>
              <li data-id="element-322">• Land with pilot cohorts</li>
              <li data-id="element-323">• Expand to full leadership population</li>
            </ul>
          </motion.div>

          {/* Stream 2 */}
          <motion.div variants={itemVariants} className={`rounded-3xl p-8 sm:p-10 ${isDark ? 'bg-white/[0.03] border border-white/10' : 'bg-white border border-heliona-beige shadow-sm'}`} data-id="element-324">
            <div className="flex items-center gap-4 mb-6" data-id="element-325">
              <div className="p-3 rounded-xl bg-heliona-orange/10" data-id="element-326">
                <TrendingUp className="w-6 h-6 text-heliona-orange" data-id="element-327" />
              </div>
              <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-328">
                Coaching Credits
              </h3>
            </div>
            <ul className={`space-y-3 text-sm sm:text-base ${isDark ? 'text-white/80' : 'text-heliona-text-gray'}`} data-id="element-329">
              <li data-id="element-330">• Incremental revenue stream</li>
              <li data-id="element-331">• Leaders book sessions via Helium Care</li>
              <li data-id="element-332">• 0% commission model attracts top talent</li>
            </ul>
          </motion.div>

          {/* Stream 3 */}
          <motion.div variants={itemVariants} className={`rounded-3xl p-8 sm:p-10 ${isDark ? 'bg-white/[0.03] border border-white/10' : 'bg-white border border-heliona-beige shadow-sm'}`} data-id="element-333">
            <div className="flex items-center gap-4 mb-6" data-id="element-334">
              <div className="p-3 rounded-xl bg-heliona-navy/10" data-id="element-335">
                <Handshake className="w-6 h-6 text-heliona-navy" data-id="element-336" />
              </div>
              <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-337">
                Partner Services
              </h3>
            </div>
            <ul className={`space-y-3 text-sm sm:text-base ${isDark ? 'text-white/80' : 'text-heliona-text-gray'}`} data-id="element-338">
              <li data-id="element-339">• Revenue share on specialized programs</li>
              <li data-id="element-340">• Delivered by network providers</li>
              <li data-id="element-341">• High-margin referral model</li>
            </ul>
          </motion.div>
        </div>

        <motion.div variants={itemVariants} className={`rounded-2xl p-6 sm:p-8 text-center ${isDark ? 'bg-white/5 border border-white/10' : 'bg-heliona-cream border border-heliona-beige'}`} data-id="element-342">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-12 text-sm sm:text-lg font-medium" data-id="element-343">
            <span className={isDark ? 'text-white' : 'text-heliona-charcoal'} data-id="element-344">
              Land: Small Pilot (10-50 leaders)
            </span>
            <span className="hidden sm:inline text-heliona-mint-dark" data-id="element-345">→</span>
            <span className={isDark ? 'text-white' : 'text-heliona-charcoal'} data-id="element-346">
              Expand: Organization-wide
            </span>
            <span className="hidden sm:inline text-heliona-mint-dark" data-id="element-347">→</span>
            <span className={isDark ? 'text-white' : 'text-heliona-charcoal'} data-id="element-348">
              Upsell: Enterprise Features
            </span>
          </div>
        </motion.div>
      </div>
    </motion.div>;
}